/**
 * Saving, and saying so.
 *
 * The owner's standing rule (see CLAUDE.md, "Saving data"): every place that
 * writes data has a Save button, shows "Unsaved changes" once something is
 * edited, "Saving…" while the write is in flight, "Saved ✓ 3:04 PM" only after
 * the database has answered without an error, and a "Not saved" message that
 * stays until it is dismissed or retried. The edits are never cleared on a
 * failure.
 *
 * This file is the logic; components/save.tsx is what it looks like. Use the
 * pair for anything new rather than hand-rolling busy flags and error strings —
 * a hand-rolled save is how "did that save or not?" happened in the first
 * place.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { PostgrestError } from "@supabase/supabase-js";
import { describeDbError } from "./dbError";
import { useUnsavedGuard } from "./unsaved";

export type SaveStatus = "clean" | "dirty" | "saving" | "saved" | "failed";

/** A failure whose message is already written for the person reading it. */
export class SaveFailure extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SaveFailure";
  }
}

class SaveTimeout extends Error {
  constructor() {
    super("timeout");
    this.name = "SaveTimeout";
  }
}

/** Long enough for a phone on one bar of LTE; short enough not to sit on
    "Saving…" forever when the connection has quietly gone. */
export const SAVE_TIMEOUT_MS = 30_000;

export function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/** fetch() failing outright looks different in every browser. */
const NETWORK = /failed to fetch|load failed|networkerror|network request failed|fetch failed|err_internet_disconnected|err_network/i;

function isPostgrestError(e: unknown): e is PostgrestError {
  return typeof e === "object" && e !== null
    && "message" in e && "code" in e && !(e instanceof Error);
}

/**
 * Why a save did not land, in plain words. Never a stack trace, never
 * "TypeError: Failed to fetch" dressed up as a database rejection.
 */
export function describeSaveError(e: unknown): string {
  const message = e instanceof Error ? e.message
    : isPostgrestError(e) ? `${e.message ?? ""} ${e.details ?? ""}`
    : typeof e === "string" ? e : "";

  if (isOffline()) {
    return "No internet connection.";
  }
  if (e instanceof SaveTimeout) {
    return "The server did not answer in time, so this is not confirmed as "
      + "saved. Check the connection.";
  }
  if (e instanceof SaveFailure) return e.message;
  if (NETWORK.test(message)) {
    return "Could not reach the server — the connection dropped or is too weak.";
  }
  if (/jwt expired|invalid jwt|not authenticated|pgrst301|refresh token/i.test(message)
      || (isPostgrestError(e) && e.code === "PGRST301")) {
    return "Your sign-in has expired. Sign in again in another tab, then press Save here.";
  }
  if (isPostgrestError(e)) return describeDbError(e);
  if (message) return message;
  return "Something went wrong.";
}

/**
 * Throw on a Supabase `{ error }`, and on an update that matched nothing.
 *
 * The second matters more than it looks: with row level security, an update
 * sent after the session has lapsed matches zero rows and returns no error at
 * all. Without asking for the rows back, that reads as a successful save.
 * Chain `.select("id")` on the update so there are rows to count.
 */
export function mustSave<T>(
  res: { data: T | null; error: PostgrestError | null },
  opts: { expectRows?: boolean } = {},
): T {
  if (res.error) throw res.error;
  if (opts.expectRows !== false && Array.isArray(res.data) && res.data.length === 0) {
    throw nothingChanged();
  }
  return res.data as T;
}

/** For a write that matched no row: see mustSave. */
export function nothingChanged(): SaveFailure {
  return new SaveFailure(
    "The database did not change anything — the record may have been deleted, "
    + "or your sign-in has expired. Reload the page and sign in again if asked.");
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new SaveTimeout()), ms);
    p.then((v) => { clearTimeout(t); resolve(v); },
           (e) => { clearTimeout(t); reject(e); });
  });
}

export const timeOfDay = (d: Date) =>
  d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

/* ---------------- toast ----------------
   For a save whose form closes behind it — an expense, a customer, a new
   deal — so the confirmation is not lost with the form. */

export interface ToastItem { id: number; text: string; tone: "ok" | "bad" }
type Listener = (items: ToastItem[]) => void;
let toasts: ToastItem[] = [];
const listeners = new Set<Listener>();
let nextId = 1;

export function subscribeToasts(fn: Listener): () => void {
  listeners.add(fn);
  fn(toasts);
  return () => { listeners.delete(fn); };
}

function emit() { for (const l of listeners) l(toasts); }

export function dismissToast(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

/** "Expense saved ✓ 3:04 PM", for five seconds. */
export function toastSaved(what: string, at: Date = new Date()) {
  const item: ToastItem = { id: nextId++, text: `${what} ✓ ${timeOfDay(at)}`, tone: "ok" };
  toasts = [...toasts.slice(-2), item];
  emit();
  setTimeout(() => dismissToast(item.id), 5000);
}

/* ---------------- the hook ---------------- */

export interface RunOptions {
  /** Toast shown on success, e.g. "Expense saved". The time is added. */
  toast?: string;
  /** Override the 30 second limit — a batch of photo uploads needs longer. */
  timeoutMs?: number;
}

export interface SaveState {
  status: SaveStatus;
  saving: boolean;
  dirty: boolean;
  savedAt: Date | null;
  /** Why the last save failed, in plain words, until dismissed or retried. */
  error: string | null;
  /**
   * Do the save. Resolves true only when `fn` resolved — so the database has
   * answered without an error. On any failure the reason is kept on `error`
   * and false comes back; nothing the person typed is touched.
   */
  run: (fn: () => Promise<unknown>, opts?: RunOptions) => Promise<boolean>;
  /** Show a failure that did not come from a save — a validation message. */
  fail: (message: string) => void;
  dismiss: () => void;
  /** Forget the last result, e.g. when the form is opened again. */
  reset: () => void;
}

/**
 * Save state for one form or one set of actions.
 *
 * @param dirty  whether the form differs from what the database holds. The
 *               caller works this out by comparing against its last saved
 *               copy, which is what makes "Unsaved changes" honest.
 * @param what   names the work for the leave-page warning ("this listing").
 *               Leave it out when the screen already has its own guard.
 */
export function useSaveState(
  { dirty = false, what }: { dirty?: boolean; what?: string } = {},
): SaveState {
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  // Set on every mount, not only the first: StrictMode mounts twice in
  // development, and a flag left false would freeze the bar on "Saving…".
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // A write still in flight is work that could be lost by closing the tab.
  useUnsavedGuard(what !== undefined && (dirty || saving), what);

  const run = useCallback(async (fn: () => Promise<unknown>, opts: RunOptions = {}) => {
    if (inFlight.current) return false;          // no double submit
    inFlight.current = true;
    setError(null);
    setSaving(true);
    try {
      if (isOffline()) throw new Error("offline");
      await withTimeout(fn(), opts.timeoutMs ?? SAVE_TIMEOUT_MS);
      const at = new Date();
      if (mounted.current) setSavedAt(at);
      if (opts.toast) toastSaved(opts.toast, at);
      return true;
    } catch (e) {
      if (mounted.current) setError(describeSaveError(e));
      return false;
    } finally {
      inFlight.current = false;
      if (mounted.current) setSaving(false);
    }
  }, []);

  const fail = useCallback((message: string) => { setError(message); }, []);
  const dismiss = useCallback(() => setError(null), []);
  const reset = useCallback(() => { setError(null); setSavedAt(null); }, []);

  const status: SaveStatus = saving ? "saving"
    : error ? "failed"
    : dirty ? "dirty"
    : savedAt ? "saved"
    : "clean";

  return { status, saving, dirty, savedAt, error, run, fail, dismiss, reset };
}
